import React from 'react'
import { IcaButton } from '../IcaElements/IcaButton'
import { IcaSegment } from '../IcaElements/IcaSegment'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaSelectDropDown } from '../IcaElements/IcaSelectDropDown'
import '@ica-azure/ica-elements/IcaIconSuccess'
import { formatDateToYMD } from '../../utils/dateUtils'
import { dateRangeOptions, numberOfSoldArticlesOptions } from './picklistHelpers'
import { StoreActiveCategories } from '../../types/Picklist'

export interface IPickListFilterProps {
  selectableCategories: StoreActiveCategories[]
  selectedCategories: StoreActiveCategories[]
  selectedDate: string
  filterSoldAmount: number
  closeDialog: () => void
  setSelectedCategories: (selectedCategories: StoreActiveCategories[]) => void
  setFilterDate: (date: string) => void
  setFilterSoldAmount: (soldAmount: number) => void
  setArticlesByCategory?: (value: boolean) => void
}

export default function PicklistFilter({
  selectableCategories,
  selectedCategories,
  selectedDate,
  filterSoldAmount,
  closeDialog,
  setSelectedCategories,
  setFilterDate,
  setFilterSoldAmount,
  setArticlesByCategory,
}: IPickListFilterProps) {
  const [checkedCategories, setCheckedCategories] = React.useState<StoreActiveCategories[]>(selectedCategories)
  const allCategoriesSelected = React.useMemo(() => 
    selectableCategories.length > 0 && checkedCategories.length === selectableCategories.length, 
  [checkedCategories, selectableCategories]
  )

  React.useEffect(() => {
    const validSelected = selectedCategories.filter(cat => selectableCategories.some(s => s.categoryName === cat.categoryName))
    setCheckedCategories(validSelected)
  }, [selectedCategories, selectableCategories])


  const handleSelectAllCategories = React.useCallback(() => {
    if (allCategoriesSelected) {
      setCheckedCategories([])
    } else {
      setCheckedCategories(selectableCategories)
      if (setArticlesByCategory) setArticlesByCategory(true)
      setSelectedCategories(selectableCategories)
    }
  }, [allCategoriesSelected, selectableCategories, setArticlesByCategory, setSelectedCategories])

  function findDateRange(targetDate: Date) {
    const sortedOptions = [...dateRangeOptions].sort((a, b) => b.date.getTime() - a.date.getTime())

    const foundOption = sortedOptions.find((option) => {
      return formatDateToYMD(targetDate) === formatDateToYMD(option.date)
    })
  
    return foundOption ? foundOption.id : 3
  }

  const [selectedDateRange, setSelectedDateRange] = React.useState(selectedDate ? findDateRange(new Date(selectedDate)) : 3)
  const [selectedSoldAmount, setSelectedSoldAmount] = React.useState<number>(() => numberOfSoldArticlesOptions.find((option) => option.value === filterSoldAmount)?.id ?? 0)

  const handleCategorySelection = (categoryName: string) => {
    const categoryObj = selectableCategories.find(cat => cat.categoryName === categoryName)
    if (!categoryObj) return
    setCheckedCategories((prevSelected) => {
      const isSelected = prevSelected.includes(categoryObj)
      const next = isSelected
        ? prevSelected.filter((c) => c !== categoryObj)
        : [...prevSelected, categoryObj]
      if (!isSelected && setArticlesByCategory) setArticlesByCategory(true)
      setSelectedCategories(next)
      return next
    })
  }

  const onFilterButtonClick = () => {
    setSelectedCategories(checkedCategories)
    const selectedOption = dateRangeOptions.find((option) => option.id === selectedDateRange)
    if (selectedOption) {
      const selectedFilterDate = formatDateToYMD(selectedOption.date)
      const currentDate = formatDateToYMD(new Date())
      setFilterDate(selectedFilterDate === currentDate ? '' : selectedFilterDate)
    }

    setFilterSoldAmount(numberOfSoldArticlesOptions.find((option) => option.id === selectedSoldAmount)?.value || 0)
    
    if (setArticlesByCategory) setArticlesByCategory(checkedCategories.length > 0)
    closeDialog()
  }

  return (
    <>
      <div className='pb-2'>
        <IcaSegment heading='Tid sen påfyllning'></IcaSegment>
        <IcaSelectDropDown
          label={
            dateRangeOptions.find((option) => option.id === selectedDateRange)
              ?.value || 'Välj datumintervall'
          }
        >
          {dateRangeOptions.map((option) => (
            <IcaCardRow
              key={`date-option-${option.id}`}
              onClick={() => setSelectedDateRange(option.id)}
            >
              <>
                <span>{option.value}</span>
                {option.id === selectedDateRange ? <tgdb-ica-icon-success style={{ color: '#007A1D' }}/> : null}
              </>
            </IcaCardRow>
          ))}
        </IcaSelectDropDown>
      </div>

      <div className='pb-2'>
        <IcaSegment heading='Sålda artiklar sen senaste påfyllning'></IcaSegment>
        <IcaSelectDropDown
          label={
            numberOfSoldArticlesOptions.find((option) => option.id === selectedSoldAmount)
              ?.label || 'Välj antalet sålda'
          }
        >
          {numberOfSoldArticlesOptions.map((option) => (
            <IcaCardRow
              key={`sold-amount-option-${option.id}`}
              onClick={() => setSelectedSoldAmount(option.id)}
            >
              <>
                <span>{option.label}</span>
                {option.id === selectedSoldAmount ? <tgdb-ica-icon-success style={{ color: '#007A1D' }}/> : null}
              </>
            </IcaCardRow>
          ))}
        </IcaSelectDropDown>
      </div>

      <IcaSegment heading='Kategorier'>
        <IcaCardRow noninteractive>
          <IcaCheckbox
            label='Välj alla'
            checked={allCategoriesSelected}
            onChange={handleSelectAllCategories}
          />
        </IcaCardRow>
        {selectableCategories.map((category, index) => (
          <IcaCardRow noninteractive key={`category-${index}`}>
            <IcaCheckbox
              label={category.categoryName}
              checked={checkedCategories.includes(category)}
              onChange={() => handleCategorySelection(category.categoryName)}
            />
          </IcaCardRow>
        ))}
      </IcaSegment>

      <IcaButton 
        slot='buttons'
        text='Filtrera'
        onClick={onFilterButtonClick}
        fullwidth
      />
    </>
  )
}
